import { capitalize } from "common/utils/strings";
import { NumberInputWithUsageNote } from "../../../components/NumberInputWithUsageNote";
import { Paper, Typography } from "@mui/material";
import { TurningRadiusLimitsSubform } from "portal/views/equipment/implements/TurningRadiusLimitsSubform";
import { TurningRadiusLimitsViewModel } from "portal/utils/atkEquipment";
import { TypedPathTo } from "portal/utils/typedPath";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import React from "react";

type Values = {
  toolSpeedMS: number;
  transitionSpeedMS: number;
  runUpDistanceM: number;
  straightenDistanceM: number;
  turningRadiusLimits: TurningRadiusLimitsViewModel;
};

interface ImplementManeuveringSubformProps {
  values: Values;
  basePath: TypedPathTo<Values>;
  mode: "edit" | "readonly";
}

export const ImplementManeuveringSubform = ({
  values,
  basePath,
  mode,
}: ImplementManeuveringSubformProps): JSX.Element => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();

  return (
    <Paper className="flex flex-col gap-4 p-4">
      <Typography className="col-span-full pb-4 font-bold">
        {t("views.autotractor.equipment.implements.maneuveringTitle")}
      </Typography>
      <NumberInputWithUsageNote
        className="grid-cols-4"
        numberInputProps={{
          className: "w-full",
          name: basePath.toolSpeedMS.$path,
          label: capitalize(t("models.equipment.implement.toolSpeed")),
          dimensionedNumberInputProps: {
            storedUnit: "m/s",
            displayUnits: { imperial: "mph", metric: "km/h" },
            localSystem: measurementSystem,
            disabled: mode === "readonly",
          },
        }}
        tooltipProps={{
          className: "col-span-2",
          children: t(
            "views.autotractor.equipment.implements.dimensionTooltips.toolSpeed"
          ),
        }}
      />
      <NumberInputWithUsageNote
        className="grid-cols-4"
        numberInputProps={{
          className: "w-full",
          name: basePath.transitionSpeedMS.$path,
          label: capitalize(t("models.equipment.implement.transitionSpeed")),
          dimensionedNumberInputProps: {
            storedUnit: "m/s",
            displayUnits: { imperial: "mph", metric: "km/h" },
            localSystem: measurementSystem,
            disabled: mode === "readonly",
          },
        }}
        tooltipProps={{
          className: "col-span-2",
          children: t(
            "views.autotractor.equipment.implements.dimensionTooltips.transitionSpeed"
          ),
        }}
      />
      <NumberInputWithUsageNote
        className="grid-cols-4"
        numberInputProps={{
          className: "w-full",
          name: basePath.runUpDistanceM.$path,
          label: capitalize(t("models.equipment.implement.runUpDistance")),
          dimensionedNumberInputProps: {
            storedUnit: "m",
            displayUnits: { imperial: "ft", metric: "m" },
            localSystem: measurementSystem,
            disabled: mode === "readonly",
          },
        }}
        tooltipProps={{
          className: "col-span-2",
          children: t(
            "views.autotractor.equipment.implements.dimensionTooltips.runUpDistance"
          ),
        }}
      />
      <NumberInputWithUsageNote
        className="grid-cols-4"
        numberInputProps={{
          className: "w-full",
          name: basePath.straightenDistanceM.$path,
          label: capitalize(t("models.equipment.implement.straightenDistance")),
          dimensionedNumberInputProps: {
            storedUnit: "m",
            displayUnits: { imperial: "ft", metric: "m" },
            localSystem: measurementSystem,
            disabled: mode === "readonly",
          },
        }}
        tooltipProps={{
          className: "col-span-2",
          children: t(
            "views.autotractor.equipment.implements.dimensionTooltips.straightenDistance"
          ),
        }}
      />
      <TurningRadiusLimitsSubform
        values={values.turningRadiusLimits}
        basePath={basePath.turningRadiusLimits}
        disabled={mode === "readonly"}
      />
    </Paper>
  );
};
